import {
  EDP_HX_ID,
  PHILOSOPHIES,
  USER_GOALS,
  TASK_FLOW_PHASES,
  WORKFLOW_TYPES,
  JOURNEYS,
  ONBOARDING_MILESTONES,
  HX_METRICS,
  DEMO_DECISION,
  buildHXDashboard,
} from '../../hx/framework';
import { TaskFlowStepper, JourneyCard, DecisionCard, ProgressTracker } from '../../components/hx';
import { Card, KpiCard } from '../../components/ui';
import { cn } from '../../lib/cn';

const DEMO_PROGRESS = [
  { label: 'Select Listing', completed: true },
  { label: 'AI Audit', completed: true },
  { label: 'Review Recommendations', active: true },
  { label: 'Apply Changes', completed: false },
  { label: 'Publish', completed: false },
];

export function HumanExperiencePage() {
  const dashboard = buildHXDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EDP_HX_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Human Experience</h1>
        <p className="mt-1 text-sm text-slate-500">
          User journeys, task flows, cognitive workflow standards, decision design, and onboarding — goal-oriented, not feature-centric.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="User Goals" value={dashboard.user_goals.toString()} />
        <KpiCard label="Journeys" value={dashboard.journeys.toString()} />
        <KpiCard label="Task Flow Phases" value={dashboard.task_flow_phases.toString()} />
        <KpiCard label="HX Metrics" value={dashboard.hx_metrics.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">HX Philosophy</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PHILOSOPHIES.map((p) => (
            <Card key={p.id} padding="sm">
              <p className="font-medium">{p.label}</p>
              <p className="mt-1 text-xs text-slate-500">{p.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">User Goals</h2>
        <p className="mt-1 text-sm text-slate-500">Experiences modeled around business objectives, not module names.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {USER_GOALS.map((g) => (
            <span key={g.id} className="rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-300">
              {g.label}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Task Flow Architecture</h2>
        <p className="mt-1 text-sm text-slate-500">Every task follows: Goal → Context → AI Analysis → … → Learning</p>
        <Card className="mt-4">
          <TaskFlowStepper activePhase="decision" />
        </Card>
        <div className="mt-3 flex flex-wrap gap-2">
          {TASK_FLOW_PHASES.map((p) => (
            <span key={p.id} className="text-xs text-slate-400">{p.label}</span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Workflow Types</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {WORKFLOW_TYPES.map((w) => (
            <span key={w.id} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">
              {w.label}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">User Journeys</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {JOURNEYS.map((j) => (
            <JourneyCard key={j.id} journey={j} />
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Decision Design</h2>
          <p className="mt-1 text-sm text-slate-500">Recommended option pre-selected with evidence, impact, and rollback.</p>
          <div className="mt-4">
            <DecisionCard decision={DEMO_DECISION} />
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Progress Experience</h2>
          <p className="mt-1 text-sm text-slate-500">Listing Optimization journey — step 3 of 5.</p>
          <Card className="mt-4">
            <ProgressTracker steps={DEMO_PROGRESS} estimatedMinutes={12} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Onboarding Milestones</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ONBOARDING_MILESTONES.map((m, i) => (
            <span key={m} className={cn(
              'rounded-md px-2 py-1 text-xs',
              i < 3 ? 'bg-success-50 text-success-700 dark:bg-success-950 dark:text-success-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800',
            )}>
              {i < 3 ? '✓ ' : ''}{m}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">HX Measurement KPIs</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {HX_METRICS.map((m) => (
            <Card key={m.id} padding="sm">
              <p className="text-sm font-medium">{m.label}</p>
              <p className="mt-1 text-xs text-slate-400">Target: {m.direction}</p>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-brand-200 bg-brand-50/30 dark:border-brand-900 dark:bg-brand-950/20">
        <p className="text-sm font-medium">Developer Implementation</p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Registry: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">pkg/hx/</code> ·
          Components: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">components/hx/</code> ·
          Onboarding: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">OnboardingWizardPage</code>
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/edp/EDP-00005_ENTERPRISE_HUMAN_EXPERIENCE.md</code>
        </p>
      </Card>
    </div>
  );
}
